
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import DashboardHeader from '../../components/layout/DashboardHeader';
import { getForm, getFormResponses, exportFormResponses, getShortLivedDownloadToken, getFullShortLivedDownloadURL } from '../../services/api/formApi';

const FormResponses = () => {
    const { formId } = useParams();
    const [form, setForm] = useState(null);
    const [responsesData, setResponsesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [size] = useState(5); // Standard page size
    const [selectedCell, setSelectedCell] = useState(null); // { rowIdx, colKey }
    const [exporting, setExporting] = useState(false);

    // Toast state
    const [toastMessage, setToastMessage] = useState(null);

    // Tracks which file is currently being prepared for download
    const [downloadingFileId, setDownloadingFileId] = useState(null);

    const handleFileDownload = async (fileId) => {
        // Prevent another download while one is already being processed
        if (downloadingFileId) return;

        try {
            setDownloadingFileId(fileId);

            const type = "RESPONSE_UPLOADED_FILE";

            if (!fileId || !type) {
                throw new Error('Missing fileId or type');
            }

            const { fileDownloadToken } = await getShortLivedDownloadToken(fileId, type);
            window.location.href = (getFullShortLivedDownloadURL(fileDownloadToken))
        } catch (error) {
            setToastMessage(error.message)
            setError(error.message || 'Failed to download file');
        } finally {
            setDownloadingFileId(null);
        }
    };

    useDocumentTitle(form ? `${form.title} - Responses` : 'Form Responses');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch form details and responses in parallel
                const [formData, responses] = await Promise.all([
                    getForm(formId),
                    getFormResponses(formId, page, size)
                ]);
                setForm(formData);
                setResponsesData(responses);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (formId) {
            fetchData();
        }
    }, [formId, page, size]);

    // Clear toast after 3 seconds
    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    const handleExport = async () => {
        if (exporting) return;
        try {
            setExporting(true);
            const response = await exportFormResponses(formId);

            // Show success message
            if (response.success) {
                setToastMessage(response.message || "Export started. You will receive an email shortly.");
            }
        } catch (err) {
            console.error('Export failed:', err);
            setToastMessage(err.message || "Failed to export responses. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < (responsesData?.rows?.totalPages || 0)) {
            setPage(newPage);
        }
    };

    if (loading && !form) {
        return (
            <div className="min-vh-100" style={{ background: '#f8fafc' }}>
                <DashboardHeader />
                <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !form) {
        return (
            <div className="min-vh-100" style={{ background: '#f8fafc' }}>
                <DashboardHeader />
                <div className="container mt-5">
                    <div className="alert alert-danger shadow-sm border-0">
                        <h4 className="alert-heading">Error Loading Form</h4>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const { columns, rows } = responsesData || {};
    const content = rows?.content || [];
    const totalPages = rows?.totalPages || 0;
    const totalElements = rows?.totalElements || 0;

    return (
        <div className="min-vh-100" style={{ background: '#f8fafc' }}>
            {/* Toast Notification */}
            {toastMessage && (
                <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1100 }}>
                    <div className="toast show align-items-center text-white bg-dark border-0" role="alert" aria-live="assertive" aria-atomic="true">
                        <div className="d-flex">
                            <div className="toast-body">
                                {toastMessage}
                            </div>
                            <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToastMessage(null)}></button>
                        </div>
                    </div>
                </div>
            )}
            <DashboardHeader />

            <main className="container-fluid container-xl py-4 pb-5">
                <div className="dashboard-header-section mb-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-start gap-3 w-100">
                        <div className="text-center text-md-start">
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <h2 className="display-6 fw-bold mb-0">{form?.title || 'Untitled Form'}</h2>
                                <span className={`badge rounded - pill px - 3 py - 2 ${form?.status === 'PUBLISHED' ? 'bg-success-subtle text-success' :
                                    form?.status === 'DRAFT' ? 'bg-warning-subtle text-warning-emphasis' :
                                        'bg-secondary-subtle text-secondary'
                                    } `}>
                                    {form?.status}
                                </span>
                            </div>
                            <p className="text-muted mb-0 lead">{form?.description || 'No description provided'}</p>
                        </div>

                        <div className="d-flex gap-2 ms-md-auto">
                            <button
                                className="btn btn-white text-secondary fw-medium border shadow-sm d-flex align-items-center gap-2"
                                onClick={handleExport}
                                disabled={exporting}
                            >
                                {exporting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span className="d-none d-sm-inline">Exporting...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-download"></i>
                                        <span className="d-none d-sm-inline">Export</span>
                                    </>
                                )}
                            </button>
                            <button
                                className="btn btn-white text-primary fw-medium border shadow-sm d-flex align-items-center gap-2"
                                onClick={() => {
                                    setPage(0); // Reset to first page on refresh
                                    // Trigger re-fetch logic (simplified by just resetting page for now or we could separate fetch logic)
                                    // ideally we'd have a refresh function
                                    window.location.reload(); // Simple refresh for now
                                }}
                            >
                                <i className="fa fa-refresh"></i>
                                <span className="d-none d-sm-inline">Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
                    <div className="card-header bg-white border-bottom border-light p-4 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                            Responses <span className="badge bg-light text-primary rounded-pill ms-2">{totalElements}</span>
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary spinner-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}

                        {!loading && (!columns || content.length === 0) ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa fa-inbox mb-3" style={{ fontSize: '48px', opacity: 0.2 }}></i>
                                <p>No responses yet.</p>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto', border: '1px solid rgba(0,0,0,0.1)' }}>
                                <table className="table mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                    <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                        <tr>
                                            {/* Row Number Column Header */}
                                            <th className="bg-light text-muted text-center" style={{
                                                width: '50px',
                                                position: 'sticky',
                                                top: 0,
                                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                borderRight: '1px solid rgba(0,0,0,0.1)'
                                            }}>#</th>
                                            {columns && columns.map((col) => (
                                                <th key={col.key} className="py-2 px-3 bg-light text-secondary fw-semibold text-nowrap" style={{
                                                    fontSize: '0.85rem',
                                                    minWidth: '150px',
                                                    position: 'sticky',
                                                    top: 0,
                                                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                    borderRight: '1px solid rgba(0,0,0,0.1)'
                                                }}>
                                                    {col.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {content.map((row, idx) => (
                                            <tr key={idx}>
                                                {/* Row Number */}
                                                <td className="text-center text-muted bg-light" style={{
                                                    fontSize: '0.8rem',
                                                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                    borderRight: '1px solid rgba(0,0,0,0.1)'
                                                }}>
                                                    {page * size + idx + 1}
                                                </td>
                                                {columns.map((col) => {
                                                    const value = row[col.key];
                                                    let displayValue = value;

                                                    if (col.type === 'DATE' && value) {
                                                        displayValue = new Date(value).toLocaleString();
                                                    } else if (value === null || value === undefined) {
                                                        displayValue = <span className="text-muted fst-italic opacity-50">-</span>;
                                                    }

                                                    const isFile = col.type === 'FILE_UPLOAD' /* || (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) */;
                                                    const isSelected = selectedCell?.rowIdx === idx && selectedCell?.colKey === col.key;

                                                    return (
                                                        <td
                                                            key={`${idx}-${col.key}`}
                                                            className="p-0 position-relative"
                                                            style={{
                                                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                                borderRight: '1px solid rgba(0,0,0,0.1)',
                                                                outline: isSelected ? '2px solid #1a73e8' : 'none',
                                                                zIndex: isSelected ? 20 : 1,
                                                                cursor: 'default'
                                                            }}
                                                            onClick={() => {
                                                                const selection = window.getSelection();
                                                                if (selection.toString().length === 0) {
                                                                    setSelectedCell({ rowIdx: idx, colKey: col.key });
                                                                }
                                                            }}
                                                        >
                                                            {isFile && value ? (
                                                                <div
                                                                    className="d-flex align-items-center p-2"
                                                                    style={{ height: '60px', minWidth: '200px' }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2 px-3 py-1 fw-medium shadow-sm"
                                                                        style={{
                                                                            borderRadius:
                                                                                '20px',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }}

                                                                        // Disable ALL
                                                                        // file buttons
                                                                        // while one
                                                                        // is processing
                                                                        disabled={
                                                                            downloadingFileId !==
                                                                            null
                                                                        }

                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            e.stopPropagation();
                                                                            handleFileDownload(value);
                                                                        }}
                                                                    >

                                                                        {downloadingFileId ===
                                                                            value ? (
                                                                            <>
                                                                                <span
                                                                                    className="spinner-border spinner-border-sm"
                                                                                    role="status"
                                                                                    aria-hidden="true"
                                                                                ></span>

                                                                                <span>
                                                                                    Downloading...
                                                                                </span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i
                                                                                    className="fa fa-download"
                                                                                    aria-hidden="true"
                                                                                ></i>

                                                                                <span>
                                                                                    Download
                                                                                    File
                                                                                </span>
                                                                            </>
                                                                        )}

                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <textarea
                                                                    readOnly
                                                                    value={displayValue || ''}
                                                                    className="form-control border-0 bg-transparent custom-scrollbar shadow-none"
                                                                    style={{
                                                                        height: '60px',
                                                                        minWidth: '200px',
                                                                        fontSize: '0.9rem',
                                                                        resize: 'none',
                                                                        cursor: 'text'
                                                                    }}
                                                                />
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="card-footer bg-white border-top border-light p-3">
                            <nav aria-label="Responses pagination">
                                <ul className="pagination justify-content-center mb-0">
                                    <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 text-secondary" onClick={() => handlePageChange(page - 1)} disabled={page === 0}>
                                            <i className="fa fa-chevron-left me-1"></i> Prev
                                        </button>
                                    </li>
                                    <li className="page-item disabled">
                                        <span className="page-link border-0 text-dark fw-medium">
                                            Page {page + 1} of {totalPages}
                                        </span>
                                    </li>
                                    <li className={`page-item ${page === totalPages - 1 ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 text-secondary" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1}>
                                            Next <i className="fa fa-chevron-right ms-1"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default FormResponses;

